import { useRouter } from "next/navigation";
import { FC, useRef } from "react";
import Image from "next/image";
import { useSelector } from "react-redux";
import "swiper/css";
import "swiper/css/navigation";
import { ICasinoGroupViewModel } from "@/fe-core/meta/interfaces/casino";
import { groupsSelector } from "@/fe-core/_redux/selectors/casinoSelectors";
import { isUserAuthenticatedSelector } from "@/fe-core/_redux/selectors/sessionSelectors";
import { useScreen } from "../../context/ScreenContext";
import SVGIcon from "../SVGIcon";
import Skeleton from "react-loading-skeleton";
import { useTranslations } from "next-intl";
import { I18N_NAMESPACE } from "@/config/i18n";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import useTracking from "../Hooks/useTracking";

type GroupSliderProps = {
  groupId: string | null;
};

const GroupsSlider: FC<GroupSliderProps> = ({ groupId }) => {
  const router = useRouter();
  const { navigate } = useTracking();
  const t = useTranslations(I18N_NAMESPACE.GENERAL);
  const isUserAuthenticated = useSelector(isUserAuthenticatedSelector);
  const groups: ICasinoGroupViewModel[] | null = useSelector(groupsSelector);
  const { isMobile, isTablet } = useScreen();
  const scrollTargetRef = useRef<null | HTMLDivElement>(null);

  const onClickGroupIcon = (slug: string) => {
    if (slug !== decodeURIComponent(groupId || "")) {
      router.push(`/casino/${slug}`, { scroll: false });
      navigate(`/casino/${slug}`, {
        component: "casinoNavigation - GroupsSlider",
        label: name,
        navigateFrom: "casino page",
      });
    }
  };

  const getGroupsIcons = () => {
    const groupsIconsContent: JSX.Element[] = [];
    const isForYou = groupId && groupId === "for-you";

    groupsIconsContent.push(
      <SwiperSlide key={"group-for-you"} style={{ width: "auto" }}>
        <div
          className={`text-[#868686] flex flex-col cursor-pointer hover:text-primary justify-center items-center gap-1 text-xs font-bold  ${
            isForYou ? "text-primary" : "  "
          }`}
          onClick={() => {
            router.push(`/casino`);
            navigate(`/casino`, {
              component: "casinoNavigation - GroupsSlider",
              label: t("for-you"),
              navigateFrom: "casino page",
            });
          }}
          key={"group-favorites"}
        >
          <SVGIcon icon="casino-lobby" className={`w-5 h-5`} />
          <div className="z-10">{t("for-you")}</div>
        </div>
      </SwiperSlide>
    );

    if (isUserAuthenticated) {
      const isFavoritesActive = groupId && groupId === "favorites";

      groupsIconsContent.push(
        <SwiperSlide key={"group-favorites"} style={{ width: "auto" }}>
          <Link
            href={"/casino/favorites"}
            className={`text-[#868686] flex flex-col cursor-pointer hover:text-primary justify-center items-center  gap-1.5 text-xs font-bold  ${
              isFavoritesActive ? "text-primary" : "  "
            }`}
            onClick={() => {
              navigate(`/casino/favorites`, {
                component: "casinoNavigation - GroupsSlider",
                label: t("favorite"),
                navigateFrom: "casino page",
              });
              // router.push(`/casino/favorites`);
            }}
            key={"group-favorites"}
          >
            <SVGIcon icon="favorites" className={`w-5 h-5`} />
            <div className="z-10">{t("favorite")}</div>
          </Link>
        </SwiperSlide>
      );

      const isRecentPlayedActive = groupId === "recent-games";

      groupsIconsContent.push(
        <SwiperSlide key={"group-recent-played"} style={{ width: "auto" }}>
          <Link
            href="/casino/recent-games"
            className={`text-[#868686] flex flex-col cursor-pointer hover:text-primary justify-center items-center  gap-1.5 text-xs font-bold  ${
              isRecentPlayedActive ? "text-primary" : "  "
            }`}
            onClick={() => {
              navigate(`/casino/recent-games`, {
                component: "casinoNavigation - GroupsSlider",
                label: t("recently-played"),
                navigateFrom: "casino page",
              });
              // router.push(`/casino/recent-games`);
            }}
            key={"group-recent-played"}
          >
            <SVGIcon icon="recent-games" className={`w-5 h-5`} />
            <div className="z-10">{t("recently-played")}</div>
          </Link>
        </SwiperSlide>
      );
    }

    if (groups) {
      for (let i = 0; i < groups?.length; i++) {
        const groupName = groups?.[i]?.name;
        const groupIcon = groups[i].icon?.url;

        const isActive =
          decodeURIComponent(groupId as string)
            .split("$")
            .pop() === groups?.[i]?.slug
            ? "active"
            : "";

        groupsIconsContent.push(
          <SwiperSlide key={`group-${i}`} style={{ width: "auto" }}>
            <div
              className={`text-[#868686] flex flex-col cursor-pointer hover:text-primary justify-center items-center gap-1.5 text-xs font-bold group ${
                isActive ? "text-primary" : "  "
              }`}
              onClick={() => {
                onClickGroupIcon(groups[i].slug);
              }}
              key={`group-${i}`}
            >
              <div className={"relative w-full h-5 min-w-10"}>
                <Image
                  src={
                    groupIcon
                      ? `${process.env.NEXT_PUBLIC_LOBBY_URL}${groupIcon}`
                      : `/notfound`
                  }
                  objectFit="contain"
                  fill
                  alt={groupName ? groupName : "notfound"}
                  className={`group-hover:brightness-100 group-hover:opacity-100 ${
                    isActive
                      ? "brightness-100 opacity-100"
                      : "brightness-[40] opacity-40"
                  }`}
                  loading="lazy"
                />
              </div>
              <div className="z-10">{groups[i].name}</div>
            </div>
          </SwiperSlide>
        );
      }
    }

    return groupsIconsContent;
  };

  const getNoOfSlidesPerView = () => {
    if (groups && groups.length > 1 && groups.length < 10) {
      return groups.length + (isUserAuthenticated ? 3 : 1);
    }
    return 3;
  };

  const slideCount = isMobile ? 3 : isTablet ? 6 : getNoOfSlidesPerView();

  if (!groups) {
    const skeletonSlides = [];
    for (let i = 0; i < slideCount; i++) {
      skeletonSlides.push(
        <div key={i} className="w-16 md:w-20 text-center h-6 md:h-8">
          <Skeleton
            className="block w-full h-full"
            containerClassName="overflow-auto"
          />
        </div>
      );
    }

    return (
      <div ref={scrollTargetRef} className=" relative ">
        <div
          className="box-content relative z-10 flex w-full h-full gap-4 p-0 overflow-hidden list-none transition-transform touch-pan-y"
          style={{ transform: "translate3d(0, 0, 0)" }}
        >
          {skeletonSlides}
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollTargetRef} className="flex-1 w-auto overflow-auto">
      <div className="flex w-full gap-6 ">
        <Swiper
          spaceBetween={14}
          slidesPerView={"auto"}
          freeMode={true}
          simulateTouch={true}
          scrollbar={{
            draggable: true,
          }}
          style={{ marginLeft: 0, marginRight: 0 }}
        >
          {getGroupsIcons()}
        </Swiper>
      </div>
    </div>
  );
};

export default GroupsSlider;
